/**
 * Response mapper for EntityForm.
 *
 * Provides utilities for mapping between API response/request shapes and
 * form field names using dot-notation paths. This enables the form to work
 * with APIs whose field names differ from the form's internal field names.
 *
 * @see Spec sections 2.2, 7.3, and 7.4 for mapping and error handling.
 */

// ---------------------------------------------------------------------------
// Path Utilities
// ---------------------------------------------------------------------------

/**
 * Resolves a dot-notation path on an object and returns the value.
 *
 * Supports paths like "data.user.name" which traverses nested objects.
 * Returns `undefined` if any segment along the path does not exist.
 *
 * @param obj  - The source object to read from.
 * @param path - A dot-separated string describing the property path.
 * @returns The value at the given path, or `undefined` if not found.
 *
 * @example
 * ```ts
 * const response = { data: { user: { name: 'Alice' } } };
 * getValueByPath(response, 'data.user.name'); // 'Alice'
 * getValueByPath(response, 'data.user.age');  // undefined
 * ```
 */
export function getValueByPath(obj: unknown, path: string): unknown {
  const segments = path.split('.');
  let current: unknown = obj;

  for (const segment of segments) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

/**
 * Sets a value at a dot-notation path on an object, creating intermediate
 * objects as necessary.
 *
 * Mutates the target object in place and also returns it for chaining.
 *
 * @param obj   - The target object to write to.
 * @param path  - A dot-separated string describing the property path.
 * @param value - The value to set at the given path.
 * @returns The mutated target object.
 *
 * @example
 * ```ts
 * const payload = {};
 * setValueByPath(payload, 'data.user.name', 'Alice');
 * // payload is now { data: { user: { name: 'Alice' } } }
 * ```
 */
export function setValueByPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split('.');
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    // noUncheckedIndexedAccess: segment is guaranteed to exist because
    // we iterate with i < segments.length - 1 and segments comes from split
    /* v8 ignore start */
    if (segment === undefined) continue;
    /* v8 ignore stop */

    const next = current[segment];
    if (next !== null && next !== undefined && typeof next === 'object') {
      current = next as Record<string, unknown>;
    } else {
      const newObj: Record<string, unknown> = {};
      current[segment] = newObj;
      current = newObj;
    }
  }

  const lastSegment = segments.at(-1);
  /* v8 ignore start */
  if (lastSegment !== undefined) {
    /* v8 ignore stop */
    current[lastSegment] = value;
  }

  return obj;
}

// ---------------------------------------------------------------------------
// Response -> Form Values Mapping
// ---------------------------------------------------------------------------

/**
 * Maps an API response object to form field values using the provided
 * response mapping configuration.
 *
 * For each entry in `responseMapping`:
 * - Key = form field name
 * - Value = dot-notation path into the API response
 *
 * Fields not listed in the mapping are passed through unchanged from the
 * response's top-level properties.
 *
 * @param response        - The raw API response object.
 * @param responseMapping - Mapping of form field names to response paths.
 * @returns A flat record of form field values.
 *
 * @example
 * ```ts
 * const response = { data: { first_name: 'Alice', role_id: 'admin' } };
 * const mapping = { firstName: 'data.first_name', role: 'data.role_id' };
 * mapResponseToValues(response, mapping);
 * // { firstName: 'Alice', role: 'admin' }
 * ```
 */
export function mapResponseToValues(
  response: unknown,
  responseMapping: Record<string, string> | undefined,
): Record<string, unknown> {
  // No mapping: assume the response is already a flat object matching field names
  if (!responseMapping) {
    if (response !== null && typeof response === 'object' && !Array.isArray(response)) {
      return { ...(response as Record<string, unknown>) };
    }
    return {};
  }

  const values: Record<string, unknown> = {};

  for (const [fieldName, responsePath] of Object.entries(responseMapping)) {
    values[fieldName] = getValueByPath(response, responsePath);
  }

  return values;
}

// ---------------------------------------------------------------------------
// Form Values -> Request Payload Mapping
// ---------------------------------------------------------------------------

/**
 * Maps form field values to an API request payload using the provided
 * request mapping configuration.
 *
 * For each entry in `requestMapping`:
 * - Key = form field name
 * - Value = dot-notation path in the request payload
 *
 * Fields not listed in the mapping are placed at the top level of the
 * request payload using their original field name.
 *
 * @param values         - The current form values keyed by field name.
 * @param requestMapping - Mapping of form field names to request payload paths.
 * @returns The structured request payload.
 *
 * @example
 * ```ts
 * const values = { firstName: 'Alice', role: 'admin', notes: 'VIP' };
 * const mapping = { firstName: 'first_name', role: 'role_id' };
 * mapValuesToRequest(values, mapping);
 * // { first_name: 'Alice', role_id: 'admin', notes: 'VIP' }
 * ```
 */
export function mapValuesToRequest(
  values: Record<string, unknown>,
  requestMapping: Record<string, string> | undefined,
): Record<string, unknown> {
  // No mapping: pass values through unchanged
  if (!requestMapping) {
    return { ...values };
  }

  const payload: Record<string, unknown> = {};

  for (const [fieldName, fieldValue] of Object.entries(values)) {
    const mappedPath = requestMapping[fieldName];

    if (mappedPath === undefined) {
      // No mapping for this field -- pass through at top level
      payload[fieldName] = fieldValue;
    } else {
      // Field has a mapping -- set at the mapped path
      setValueByPath(payload, mappedPath, fieldValue);
    }
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Reverse Mapping (for error field resolution)
// ---------------------------------------------------------------------------

/**
 * Inverts a mapping so that API field names resolve back to form field names.
 *
 * This is used when mapping API validation error field names (which use the
 * request mapping names) back to the form's internal field names so that
 * errors can be displayed on the correct fields.
 *
 * For dot-notation paths, the last segment is used as the API field name
 * since most validation APIs return flat field name keys.
 *
 * @param mapping - The original mapping (form field name -> API path).
 * @returns An inverted mapping (API field name -> form field name).
 *
 * @see Spec section 7.4 for error field name resolution.
 *
 * @example
 * ```ts
 * const requestMapping = { firstName: 'first_name', role: 'role_id' };
 * reverseMapping(requestMapping);
 * // { first_name: 'firstName', role_id: 'role' }
 * ```
 */
export function reverseMapping(
  mapping: Record<string, string> | undefined,
): Record<string, string> {
  if (!mapping) return {};

  const reversed: Record<string, string> = {};

  for (const [formFieldName, apiPath] of Object.entries(mapping)) {
    // Use the full path as the key, since some APIs might return
    // nested error keys. Also add the last segment as a fallback key.
    reversed[apiPath] = formFieldName;

    const segments = apiPath.split('.');
    const lastSegment = segments.at(-1);
    if (lastSegment !== undefined && lastSegment !== apiPath) {
      // Only set the short key if it doesn't already map to another field
      reversed[lastSegment] ??= formFieldName;
    }
  }

  return reversed;
}
